import React, { createContext, useContext, useState, useEffect } from 'react';
import { Moon, Sun } from 'lucide-react';
import { useLocation } from 'react-router-dom';

// Tauri title bar theme sync
let tauriWindow = null;
if (window.__TAURI_INTERNALS__) {
  import('@tauri-apps/api/window').then(mod => {
    tauriWindow = mod.getCurrentWindow();
  });
}

const ThemeContext = createContext({
  isDarkMode: false,
  toggleTheme: () => {}
});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const colors = {
  dark: {
    background: {
      primary: '#0D0E0E',      // Softer than pure black
      secondary: '#0D0E0E',
      card: '#0D0E0E',
      elevated: '#0D0E0E'
    },
    border: {
      subtle: '#2a2a2a',
      default: '#333333',
      emphasis: '#404040'
    },
    text: {
      primary: '#e8e8e8',
      secondary: '#a8a8a8',
      tertiary: '#888888'
    }
  },
  light: {
    background: {
      primary: '#F5F5F5',      // Warmer white
      secondary: '#F5F5F5',
      card: '#F5F5F5',
      elevated: '#F5F5F5'
    },
    border: {
      subtle: '#e8ebed',
      default: '#d1d5db',
      emphasis: '#9ca3af'
    },
    text: {
      primary: '#1a1a1a',
      secondary: '#4b5563',
      tertiary: '#6b7280'
    }
  }
};


export const ThemeProvider = ({ children }) => {
  
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try { return localStorage.getItem('dq_dark_mode') === 'true'; } catch { return false; }
  });

  useEffect(() => {
    document.body.style.backgroundColor = isDarkMode ? '#0D0E0E' : '#F5F5F5';
    document.body.style.color = isDarkMode ? '#BBC2D4' : '#3b4046ff';
    document.documentElement.style.backgroundColor = isDarkMode ? '#0D0E0E' : '#F5F5F5';
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');

    try { localStorage.setItem('dq_dark_mode', String(isDarkMode)); } catch {}

    // Sync Tauri title bar theme
    if (tauriWindow) {
      tauriWindow.setTheme(isDarkMode ? 'dark' : 'light');
    }
  }, [isDarkMode]);

  const toggleTheme = () => setIsDarkMode(prev => !prev);

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const ThemeSwitcher = () => {
  
  const { isDarkMode, toggleTheme } = useTheme();
  
  const location = useLocation();
  
  // Hide the theme switcher in the app
  if (location.pathname === '/app') {
    return null;
  }

  return (
    <button
      onClick={toggleTheme}
      style={{
        position: 'fixed',
        top: '10px',
        right: '15px',
        padding: '0px',
        width: '30px',
        height: '30px',
        borderRadius: '50%',
        cursor: 'pointer',
        backgroundColor: isDarkMode ? '#333' : '#fff',
        border: `1px solid ${isDarkMode ? '#444' : '#e0e0e0'}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'background-color 0.2s ease-in-out',
        zIndex: 1000
      }}
      aria-label="Toggle theme"
    >
      {isDarkMode ? (
        <Sun className="w-6 h-6" color="#BBC2D4" />
      ) : (
        <Moon className="w-6 h-6" color="#2C3E50" />
      )}
    </button>
  );
};